import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShoppingCart, Heart } from 'lucide-react'
import { toast } from 'sonner'
import type { ProductCard as ProductCardType } from '../../types'
import { truncate, cleanBrDescription } from '../../lib/utils'
import { useCart } from '../../context/CartContext'
import { useWishlist } from '../../context/WishlistContext'
import { useLocalePath } from '../layout/LanguageRoute'
import StockBadge from './StockBadge'
import LifecycleBadge from './LifecycleBadge'
import DeliveryBadge from './DeliveryBadge'
import PriceDisplay from './PriceDisplay'

interface ProductCardProps {
  product: ProductCardType
}

export default function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCart()
  const { isInWishlist, addItem: addToWishlist, removeItem: removeFromWishlist, items: wishlistItems } = useWishlist()
  const lp = useLocalePath()
  const { t } = useTranslation()

  const inWishlist = isInWishlist(product.normalized_mpn)

  function handleAddToCart(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    addItem({
      normalizedMpn: product.normalized_mpn,
      slug: product.slug,
      displayTitle: product.display_title,
      effectivePrice: product.effective_price,
      effectivePriceIncVat: product.effective_price_inc_vat,
      primaryImageUrl: product.primary_image_url,
      moq: product.moq || 1,
    })
    toast.success(t('shop.product.addedToCart'), { description: product.display_title })
  }

  function handleToggleWishlist(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (inWishlist) {
      const entry = wishlistItems.find(i => i.normalizedMpn === product.normalized_mpn)
      if (entry) removeFromWishlist(entry.uuid)
    } else {
      addToWishlist(product.normalized_mpn)
    }
  }

  return (
    <Link
      to={lp(`/product/${product.slug}`)}
      className="flex flex-col h-full group rounded-lg border border-neutral-200 bg-white hover:border-primary hover:-translate-y-0.5 hover:shadow-sm transition-all duration-150"
    >
      {/* Image — CLS protection comes from the parent's aspect-[4/3].
          We deliberately do NOT set width/height attrs because the CDN serves
          ~250px thumbnails; declaring 400x300 makes the browser bilinear-upscale
          a too-small source on retina (DPR 2/3), which looks blurry. The CSS
          aspect-ratio holds the box, the img scales to fit. */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-t-lg bg-white flex items-center justify-center p-3">
        <img
          src={product.primary_image_url || '/placeholder-product.svg'}
          alt={product.display_title}
          loading="lazy"
          decoding="async"
          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-200"
          onError={e => { (e.target as HTMLImageElement).src = '/placeholder-product.svg' }}
        />
        {product.labels?.includes('new') && (
          <span className="absolute top-2 right-2 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide uppercase">
            Nieuw
          </span>
        )}
        {!product.labels?.includes('new') && (product.labels?.includes('top') || product.labels?.includes('best_seller')) && (
          <span className="absolute top-2 right-2 bg-gcg-dark text-white text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide uppercase">
            Top
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        {/* MPN */}
        <span className="font-mono text-xs text-neutral-600 tracking-wide">
          {product.normalized_mpn}
        </span>

        {/* Title */}
        <h3 className="font-semibold text-sm text-neutral-900 leading-snug group-hover:text-primary transition-colors line-clamp-2">
          {product.display_title}
        </h3>

        {/* Category / Series */}
        <span className="text-xs text-neutral-600">
          {product.category}{product.series ? ` · ${product.series}` : ''}
        </span>

        {/* Description */}
        {product.short_description && (
          <p className="text-xs text-neutral-600 leading-relaxed line-clamp-2">
            {truncate(cleanBrDescription(product.short_description), 120)}
          </p>
        )}

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mt-auto">
          <StockBadge status={product.stock_status} />
          <DeliveryBadge speed={product.delivery_speed} />
          <LifecycleBadge phase={product.lifecycle_phase} />
        </div>

        {/* Price + Add to Cart + Wishlist */}
        <div className="pt-2 border-t border-neutral-100 flex items-end justify-between gap-2">
          <PriceDisplay
            effective_price={product.effective_price}
            effective_price_inc_vat={product.effective_price_inc_vat}
            price_formatted={product.price_formatted}
          />
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={handleToggleWishlist}
              className={`p-2 rounded-md border transition-colors ${inWishlist ? 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100' : 'border-neutral-200 text-neutral-400 hover:border-red-200 hover:text-red-400'}`}
              aria-label={inWishlist ? t('shop.product.removeFromWishlist') : t('shop.product.addToWishlist')}
            >
              <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
            </button>
            {product.effective_price !== null && (
              <button
                onClick={handleAddToCart}
                className="p-2 rounded-md bg-primary text-white hover:bg-primary-hover transition-colors"
                aria-label={t('shop.product.addToCart')}
              >
                <ShoppingCart className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}
